// Badge — Linear-style pill badges with dot indicator support
// Semantic variants: success/warning/error/info/neutral
// New variant "dot": colored dot prefix + label text
// New variant "outline": border-only, no fill

export const badgeStyles = {
  components: {
    Badge: {
      baseStyle: {
        textTransform: 'uppercase',
        fontWeight: 'semibold',
        fontSize: '10px',
        borderRadius: 'full',
        px: '8px',
        py: '2px',
        letterSpacing: '0.06em',
        // tabular-nums keeps numeric badges from shifting width
        fontVariantNumeric: 'tabular-nums',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: '1.4',
      },
      variants: {
        // Semantic status variants — soft filled pill
        success: {
          bg: 'green.50',
          color: 'green.700',
          borderWidth: '1px',
          borderColor: 'green.200',
        },
        warning: {
          bg: 'orange.50',
          color: 'orange.700',
          borderWidth: '1px',
          borderColor: 'orange.200',
        },
        error: {
          bg: 'red.50',
          color: 'red.700',
          borderWidth: '1px',
          borderColor: 'red.200',
        },
        info: {
          bg: 'blue.50',
          color: 'blue.700',
          borderWidth: '1px',
          borderColor: 'blue.200',
        },
        neutral: {
          bg: 'gray.100',
          color: 'gray.600',
          borderWidth: '1px',
          borderColor: 'gray.200',
        },
        // Brand badge — emerald pill, used for "active", "new", etc.
        brand: {
          bg: 'brand.50',
          color: 'brand.700',
          borderWidth: '1px',
          borderColor: 'brand.200',
        },
        // Solid: high-contrast, use sparingly (count indicators, etc.)
        solid: props => ({
          bg: props.colorScheme === 'brand' ? 'brand.500' : `${props.colorScheme}.500`,
          color: 'white',
          borderWidth: 0,
        }),
        // Subtle: normalised Chakra-like, picks up colorScheme
        subtle: props => ({
          bg: props.colorScheme === 'brand' ? 'brand.50' : `${props.colorScheme}.50`,
          color: props.colorScheme === 'brand' ? 'brand.700' : `${props.colorScheme}.700`,
          borderWidth: '1px',
          borderColor: props.colorScheme === 'brand' ? 'brand.200' : `${props.colorScheme}.200`,
        }),
        // Outline: border only, transparent bg — for secondary status labels
        outline: props => ({
          bg: 'transparent',
          color: props.colorScheme === 'brand' ? 'brand.600' : `${props.colorScheme}.600`,
          borderWidth: '1px',
          borderColor: props.colorScheme === 'brand' ? 'brand.400' : `${props.colorScheme}.400`,
        }),
        // Dot: small colored circle + label — Linear/Notion status indicator style.
        // Usage: <Badge variant="dot" colorScheme="green">Active</Badge>
        // The dot is rendered via CSS pseudo-element on the Badge's _before.
        // Caller wraps text; dot auto-prepends via ::before.
        dot: props => {
          const schemeMap = {
            green:  { dot: '#16A34A', bg: 'green.50',  text: 'green.700',  border: 'green.200'  },
            orange: { dot: '#F59E0B', bg: 'orange.50', text: 'orange.700', border: 'orange.200' },
            red:    { dot: '#EF4444', bg: 'red.50',    text: 'red.700',    border: 'red.200'    },
            blue:   { dot: '#0EA5E9', bg: 'blue.50',   text: 'blue.700',   border: 'blue.200'   },
            gray:   { dot: '#9CA3AF', bg: 'gray.100',  text: 'gray.600',   border: 'gray.200'   },
            brand:  { dot: '#6366F1', bg: 'brand.50',  text: 'brand.700',  border: 'brand.200'  },
          };
          const s = schemeMap[props.colorScheme] || schemeMap.gray;
          return {
            bg: s.bg,
            color: s.text,
            borderWidth: '1px',
            borderColor: s.border,
            gap: '5px',
            _before: {
              content: '""',
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: 'full',
              bg: s.dot,
              flexShrink: 0,
            },
          };
        },
      },
      sizes: {
        sm: { px: '6px',  py: '1px', fontSize: '10px' },
        md: { px: '8px',  py: '2px', fontSize: '12px' },
        lg: { px: '10px', py: '3px', fontSize: '14px' },
      },
      defaultProps: {
        variant: 'subtle',
        size: 'md',
      },
    },
  },
};
